import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { CommandRequestBuilderService } from '@app/services/command-request-builder.service';
import { LoggerService } from '@app/services/logger.service';
import { getServiceEntitlements } from '@app/shared/utils';
import { CampaignType, ControlPageFacade, SignalRFacade, SnackBarFacade } from '@ra-state';
import { CoreConfiguration } from '@rockwell-automation-inc/service';
import { DateTime } from 'luxon';
import * as R from 'remeda';
import {
  catchError,
  concatMap,
  EMPTY,
  filter,
  finalize,
  map,
  Observable,
  of,
  skipWhile,
  switchMap,
  take,
  tap,
  throwError,
  timeout,
  TimeoutError,
} from 'rxjs';
import { ErroMessageMap, ErrorPartMessageMapper } from 'src/app/common-constants';
import { CommandRequest, CustomerSupportEvents, TrialEvents } from 'src/app/services/command-request.service';
import {
  CampaignDetails,
  EffectiveRole,
  Entitlement,
  EntitlementContext,
  EntitylementTypes,
  ErrorContext,
  ICatalogInfo,
  IDomainMessage,
  IDomainUpdateResponse,
  IStreamStatus,
  ITenantDetail,
  Organizations,
  PaginatedRequest,
  PaginatedResponse,
  Pagination,
  ServiceResolutionData,
  ServiceResolutionError,
  SortFilterContext,
  StreamHandler,
  StreamState,
  StreamStateZERO,
  StreamStatus,
  TenantHierarchyUsersRequest,
  TenantListPage,
  TenantUser,
  TenantUserAccessRecord,
  TenantUtilityTokens,
  TenantUtilityTokensResponse,
  TrialBundle,
  TrialCampaignStatus,
  UserState,
} from '../../+state/common-admin.model';

class ErrorContextImpl implements ErrorContext {
  url: string;
  message: string;
  correlationId: string | undefined;
  constructor(private readonly httpError: HttpErrorResponse) {
    this.url = this.httpError.url || '';
    this.message = `${this.httpError.message}`;
    this.correlationId = String(httpError.headers.get('RequestId') || 'Not Available');
  }
}
interface ICatalogMetadata {
  productNumber: string;
  name: string;
  description: string;
  attributes: [];
  serviceKind: string;
}

class StreamStatusImpl implements IStreamStatus {
  constructor(private readonly status: StreamStatus) {}
  get tooltip(): string {
    const projector = this.getHandlerState(StreamHandler.Projector);
    const reactor = this.getHandlerState(StreamHandler.Reactor);
    if (this.isHealthy) {
      return `Stream is healthy. Projector Index: ${projector.index}, Reactor Index: ${reactor.index}`;
    }
    let msg = '';
    if (projector.dead) {
      msg += `Projector is dead. Index: ${projector.index}. `;
    }
    if (reactor.dead) {
      msg += `Reactor is dead. Index: ${reactor.index}. `;
    }
    return `Unhealthy; View is out of date. ${msg} `;
  }
  getHandlerState(handler: StreamHandler): StreamState {
    return this.status?.[handler] || StreamStateZERO;
  }
  get isHealthy(): boolean {
    return !this.isUnhealthy;
  }
  get isUnhealthy(): boolean {
    return this.status && Object.values(this.status).some((it) => it.dead);
  }
}
@Injectable({
  providedIn: 'root',
})
export class DataService {
  private readonly http = inject(HttpClient);
  private readonly controlPageFacade = inject(ControlPageFacade);
  private readonly signalRFacade = inject(SignalRFacade);
  private readonly router = inject(Router);
  private readonly snackBarFacade = inject(SnackBarFacade);
  private readonly commandRequestBuilderService = inject(CommandRequestBuilderService);
  private readonly configService = inject<CoreConfiguration>('configNav' as any);

  baseUrl: string;
  logger = inject(LoggerService).withContext(DataService);

  constructor() {
    this.baseUrl = this.configService.csApiBaseUrl;
  }

  getStreamStatus$(streamId: string): Observable<IStreamStatus> {
    const url = `${this.baseUrl}/stream/${streamId}`;
    return this.http.get(url).pipe(
      filter((response: any) => Object.keys(response).length > 0),
      map((response: any) => {
        const status = response as StreamStatus;
        return new StreamStatusImpl(status);
      }),
    );
  }

  getRecords$(recordType: string, pageRequest: EntitlementContext, sortFilterContext?: any): Observable<any> {
    let headers = new HttpHeaders();
    const url = `${this.baseUrl}/${recordType}`;
    let reqParams = new HttpParams();
    reqParams = reqParams.append('perPage', pageRequest.perPage);
    reqParams = reqParams.append('pageNum', pageRequest.pageNum);
    reqParams = reqParams.append('sortFilterOpts', JSON.stringify(sortFilterContext));
    if (pageRequest.email) {
      reqParams = reqParams.append('email', pageRequest.email);
    }

    if (pageRequest.contractNo) {
      reqParams = reqParams.append('contractNo', pageRequest.contractNo);
    }
    if (pageRequest.tenantId) {
      headers = this.setTenantIdHeader(headers, pageRequest.tenantId);
    }

    return this.http.get<PaginatedResponse<any>>(url, {
      headers: headers,
      params: reqParams,
    });
  }

  getUserPreferences$(userId: string): Observable<UserState> {
    const url = `${this.baseUrl}/users/${userId}/preferences`;
    return this.http.get<UserState>(url);
  }

  getUserOrganisations$(userId: string): Observable<Organizations[]> {
    const url = `${this.baseUrl}/users/${userId}/tenants`;
    return this.http.get<Organizations[]>(url);
  }

  getUserEntitlements$(
    userId: string,
    pageRequest: Pagination,
    sortFilterContext: SortFilterContext,
  ): Observable<PaginatedResponse<Entitlement>> {
    const url = `${this.baseUrl}/users/${userId}/entitlements?perPage=${pageRequest.perPage}&pageNum=${pageRequest.pageNum}`;
    const sortFilterModel = sortFilterContext || {};
    return this.http
      .get<PaginatedResponse<Entitlement>>(url, {
        headers: new HttpHeaders(),
        params: {
          sortFilterOpts: JSON.stringify(sortFilterModel),
        },
      })
      .pipe(
        map((data) => {
          data.records.forEach((entitlement) => {
            // Remove this piece of code when the backend provides the correct combineType
            if (R.isEmpty(entitlement.attributes?.combineType) && entitlement.serviceKind === '') {
              entitlement.serviceKind = 'UniversalCredits';
              entitlement.attributes = {
                combineType: EntitylementTypes.utilityToken,
              };
            }
          });

          return data;
        }),
        catchError((error: unknown) => this.handleError$(error)),
      );
  }

  getTenantUsers$(payload: PaginatedRequest): Observable<any> {
    let url = `${this.baseUrl}${ApiEndPoints.Tenant}users`;
    if (payload.perPage) {
      url = `${this.baseUrl}${ApiEndPoints.Tenant}users?perPage=${payload.perPage}&pageNum=${payload.pageNum}`;
    }

    const sortFilterModel = payload.sortFilterContext ?? {};
    return this.http
      .get<PaginatedResponse<any>>(url, {
        headers: this.setTenantIdHeader(new HttpHeaders(), payload.tenantId),
        params: {
          sortFilterOpts: JSON.stringify(sortFilterModel),
        },
      })
      .pipe(catchError((error: unknown) => this.handleError$(error)));
  }

  getTenantHierarchyUsersAccess$(payload: PaginatedRequest): Observable<PaginatedResponse<TenantUserAccessRecord>> {
    let url = `${this.baseUrl}${ApiEndPoints.Tenant}hierarchy/usersAccess`;
    if (payload.perPage) {
      url = `${url}?perPage=${payload.perPage}&pageNum=${payload.pageNum}`;
    }

    const sortFilterModel = payload.sortFilterContext ?? {};
    return this.http
      .get<PaginatedResponse<TenantUserAccessRecord>>(url, {
        headers: this.setTenantIdHeader(new HttpHeaders(), payload.tenantId),
        params: {
          sortFilterOpts: JSON.stringify(sortFilterModel),
        },
      })
      .pipe(catchError((error: unknown) => this.handleError$(error)));
  }

  getTenantHierarchyUsers$(
    tenantHierarchyUsersRequest: TenantHierarchyUsersRequest,
  ): Observable<PaginatedResponse<TenantUser>> {
    const { perPage, pageNum, includeTree, includeSiblings, tenantId, sortFilterContext } = tenantHierarchyUsersRequest;
    const url = `${this.baseUrl}${ApiEndPoints.Tenant}hierarchy/users?perPage=${perPage}&pageNum=${pageNum}&includeTree=${includeTree}&includeSiblings=${includeSiblings}`;

    return this.http
      .get<PaginatedResponse<TenantUser>>(url, {
        headers: this.setTenantIdHeader(new HttpHeaders(), tenantId),
        params: {
          sortFilterOpts: JSON.stringify(sortFilterContext ?? {}),
        },
      })
      .pipe(catchError((error: unknown) => this.handleError$(error)));
  }

  commandRequest$(commandRequest: CommandRequest): Observable<IDomainUpdateResponse> {
    return of(true).pipe(
      tap(() => {
        this.logger.debug('Spinner CommandReq: +1');
        this.controlPageFacade.incrementInFlightRequests();
      }),
      switchMap(() =>
        this.http.request(commandRequest.Method, commandRequest.ApiEndpoint, {
          body: commandRequest.RequestOptions.body,
          headers: commandRequest.RequestOptions.headers,
          observe: commandRequest.RequestOptions.observe,
          params: commandRequest.RequestOptions.params,
        }),
      ),
      switchMap((response) => this.toDomainResponse$(response, commandRequest)),
      catchError((error: unknown) => {
        return this.handleApiError$(error, commandRequest);
      }),
      finalize(() => {
        this.logger.debug('Spinner CommandReq (Finalize): -1');
        this.controlPageFacade.decrementInFlightRequests();
      }),
    );
  }

  private handleApiError$(error: any, commandRequest: CommandRequest): Observable<any> {
    if (commandRequest.errorHandler$) {
      return commandRequest.errorHandler$(error);
    }
    const httpError = error as HttpErrorResponse;
    const errorMsgMap = commandRequest.ErrorMessageMap;
    switch (httpError.status) {
      case 400: {
        let message = 'The request was invalid';
        const type = 'Error';
        const errorCode = httpError.error.errorCode;
        const errorMsgMap = ErroMessageMap[errorCode];
        if (errorMsgMap) {
          message = errorMsgMap.message;
        } else {
          const errorPartMsgMap = ErrorPartMessageMapper.getMessageIconMap(errorCode);
          if (errorPartMsgMap) {
            message = errorPartMsgMap.message;
          }
        }

        this.snackBarFacade.displayMessage({
          message: message,
          type: type,
        });
        return throwError(() => error);
      }
      case 403: {
        const message = errorMsgMap?.[403] || 'Insufficient Permissions: Redirecting to dashboard';
        this.snackBarFacade.displayMessage({
          message: message,
          type: 'Error',
        });
        return throwError(() => error);
      }
      case 409: {
        const message =
          errorMsgMap?.[409] ||
          'The request could not be completed due to a conflict with the current state of the resource.';
        this.snackBarFacade.displayMessage({
          message: message,
          type: 'Error',
        });
        return throwError(() => error);
      }
      case 429: {
        const retryAfterSeconds = DateTime.fromISO(httpError.error.retryAfter).second;
        let message = `Please retry after ${retryAfterSeconds} seconds`;
        const type = 'Error';
        const errorCode = httpError.error.errorCode;
        const errorMsgMap = ErroMessageMap[errorCode];
        if (errorMsgMap) {
          message = `${errorMsgMap.message} ${message}`;
        }
        this.snackBarFacade.displayMessage({
          message: message,
          type: type,
        });
        return EMPTY;
      }
      default: {
        this.controlPageFacade.navigateToError(new ErrorContextImpl(httpError));
        return throwError(() => error);
      }
    }
  }

  private toDomainResponse$(
    response: HttpResponse<object>,
    commandRequest: CommandRequest,
  ): Observable<IDomainUpdateResponse> {
    if (commandRequest.skipWaitingOnResponse(response.status)) {
      return of({ response } as IDomainUpdateResponse);
    }
    return this.signalRFacade.domainMessage$.pipe(
      map((dm) => dm),
      filter((domainMessage) => {
        return domainMessage?.context.correlationId === response.headers.get('RequestId');
      }),
      concatMap((domainMessage) => {
        if (!commandRequest.IsAssistedRequest) {
          return of(domainMessage);
        }
        if (domainMessage.type === CustomerSupportEvents.ServiceRequestResolved) {
          const resolutionContext = domainMessage.data as unknown as ServiceResolutionData;
          if (resolutionContext.resolutionType.Case === 'Failed') {
            const errCode = resolutionContext.resolutionData.error;
            return throwError(() => new ServiceResolutionError(errCode));
          }
        }
        return of(domainMessage);
      }),
      skipWhile((domainMessage: IDomainMessage) => {
        this.logger.log('recd domain message', domainMessage);
        return commandRequest.notEventPredicateSatisfied(domainMessage);
      }),
      take(1),
      map((domainMessage) => {
        const domainUpdateResponse: IDomainUpdateResponse = {
          message: domainMessage,
          response: response,
        };
        return domainUpdateResponse;
      }),
      timeout(commandRequest.TimeOutInMillies),
      catchError((error: unknown) => {
        if (error instanceof TimeoutError) {
          this.snackBarFacade.displayMessage({
            message: 'Operation timeout. Please refresh.',
            type: 'Error',
          });
          return throwError(() => new Error(`Timeout for ${commandRequest.ApiEndpoint}`));
        }

        if (error instanceof ServiceResolutionError) {
          const errorMsgMap = ErroMessageMap[error.errorCode];
          this.snackBarFacade.displayMessage({
            message: errorMsgMap?.message || `Service Request Failed`,
            type: 'Error',
          });
        }

        return throwError(() => error);
      }),
    );
  }

  getCatalogInfo$(): Observable<ICatalogInfo[]> {
    const url = `${this.baseUrl}${ApiEndPoints.Catalog}`;
    return this.http.get<ICatalogMetadata[]>(url).pipe(
      map((catalogResp) =>
        catalogResp.map((catalog) => {
          return {
            catalogCode: catalog.productNumber,
            description: catalog.description,
            name: catalog.name,
            attributes: catalog.attributes,
            serviceKind: catalog.serviceKind,
          };
        }),
      ),
      catchError((e: unknown) => this.handleError$(e)),
    );
  }

  getCampaignDetails$(campaignId: string): Observable<CampaignDetails> {
    const url = `${this.baseUrl}/TrialCampaigns/${campaignId}`;
    return this.http.get<CampaignDetails>(url).pipe(
      map((campaignDetails) => {
        return {
          ...campaignDetails,
          availableReservations: `${campaignDetails.remainingTrialsQuantity} / ${campaignDetails.trialsQuantity}`,
          status: TrialCampaignStatus[campaignDetails.status],
          createdByUser: campaignDetails.createdByUser ? campaignDetails.createdByUser : '-',
          endedByUser: campaignDetails.endedByUser ? campaignDetails.endedByUser : '-',
          pausedByUser: campaignDetails.pausedByUser ? campaignDetails.pausedByUser : '-',
        };
      }),
      catchError((error: unknown) => this.handleError$(error)),
    );
  }

  getTrialBundles$(campaignType?: CampaignType): Observable<TrialBundle[]> {
    const url = `${this.baseUrl}${ApiEndPoints.TrialBundles}`;
    const params = campaignType ? { bundleType: campaignType } : undefined;
    return this.http.get<TrialBundle[]>(url, { params }).pipe(
      map((trialbundles) => {
        trialbundles.forEach((trialbundle) => {
          const catalogCodes = trialbundle.products.reduce((catalogCodes: string[], product) => {
            catalogCodes.push(typeof product === 'string' ? product : product.catalog);
            return catalogCodes;
          }, []);
          if (trialbundle.credits) {
            catalogCodes.push(String(trialbundle.credits));
          }
          trialbundle.nameAndDescription = [trialbundle.name, trialbundle.description];
          trialbundle.creditsAndProductCatalogs = catalogCodes;
        });
        return trialbundles;
      }),
      catchError((error: unknown) => this.handleError$(error)),
    );
  }

  getAllocatedTrials$(payload: PaginatedRequest): Observable<any> {
    let url = `${this.baseUrl}${ApiEndPoints.Tenant}trials`;
    if (payload.perPage) {
      url = `${url}?perPage=${payload.perPage}&pageNum=${payload.pageNum}`;
    }

    const sortFilterModel = payload.sortFilterContext ?? {};
    return this.http
      .get<PaginatedResponse<any>>(url, {
        headers: this.setTenantIdHeader(new HttpHeaders(), payload.tenantId),
        params: {
          sortFilterOpts: JSON.stringify(sortFilterModel),
        },
      })
      .pipe(catchError((error: unknown) => this.handleError$(error)));
  }

  getUnallocatedTrials$(
    userId: string,
    pageRequest: Pagination,
    sortFilterContext: SortFilterContext,
  ): Observable<any> {
    let url = `${this.baseUrl}${ApiEndPoints.Users}${userId}/trials`;
    if (pageRequest.perPage) {
      url = `${url}?perPage=${pageRequest.perPage}&pageNum=${pageRequest.pageNum}`;
    }

    const sortFilterModel = sortFilterContext || {};
    return this.http
      .get<PaginatedResponse<any>>(url, {
        headers: new HttpHeaders(),
        params: {
          sortFilterOpts: JSON.stringify(sortFilterModel),
        },
      })
      .pipe(catchError((error: unknown) => this.handleError$(error)));
  }

  pauseCampaign$(campaignId: string): Observable<any> {
    const requestUrl = `/TrialCampaigns/${campaignId}/pause`;
    const commandRequest = this.commandRequestBuilderService
      .new(requestUrl, 'PUT', TrialEvents.CampaignPaused)
      .withWaitOn200Ok()
      .withErrorHandler((error: unknown) => {
        const httpError = error as HttpErrorResponse;
        if (httpError.error?.errorCode === 'InvalidTrialCampaign') {
          this.snackBarFacade.displayMessage({
            message: 'There was an error trying to pause the trial campaign: Invalid Trial Campaign.',
            type: 'Error',
          });
        } else {
          this.snackBarFacade.displayMessage({
            message: 'There was an error trying to pause the trial campaign: ' + httpError.error?.errorCode,
            type: 'Error',
          });
        }
        return throwError(() => error);
      });
    return this.commandRequest$(commandRequest);
  }

  resumeCampaign$(campaignId: string): Observable<any> {
    const requestUrl = `/TrialCampaigns/${campaignId}/resume`;
    const commandRequest = this.commandRequestBuilderService
      .new(requestUrl, 'PUT', TrialEvents.CampaignResumed)
      .withWaitOn200Ok();
    return this.commandRequest$(commandRequest).pipe(catchError((error: unknown) => this.handleError$(error)));
  }

  endCampaign$(campaignId: string): Observable<any> {
    const requestUrl = `/TrialCampaigns/${campaignId}/end`;
    const commandRequest = this.commandRequestBuilderService
      .new(requestUrl, 'PUT', TrialEvents.CampaignEnded)
      .withWaitOn200Ok();
    return this.commandRequest$(commandRequest).pipe(catchError((error: unknown) => this.handleError$(error)));
  }

  sendRedemptionLinks$(campaignId: string, reservationIds: string[]): Observable<any> {
    const url = `${this.baseUrl}/TrialCampaigns/${campaignId}/reservations/send-redemption-links`;
    return this.http.post(url, reservationIds);
  }

  setTenantIdHeader(headers: HttpHeaders, tenantId: string): HttpHeaders {
    return headers.set('tenantid', tenantId);
  }

  getTenantDetail$(tenantId: string): Observable<ITenantDetail> {
    const url = `${this.baseUrl}${ApiEndPoints.Tenant}`;
    return this.http.get<ITenantDetail>(url, { headers: this.setTenantIdHeader(new HttpHeaders(), tenantId) }).pipe(
      map((tenantDetails) => {
        tenantDetails.entitlements = getServiceEntitlements(tenantDetails);
        return tenantDetails;
      }),
      catchError((error: unknown) => this.handleError$(error)),
    );
  }

  getTenantUtilityTokens$(tenantId: string): Observable<TenantUtilityTokens> {
    const url = `${this.baseUrl}${ApiEndPoints.UtilityTokens}`;
    return this.http
      .get<TenantUtilityTokensResponse>(url, { headers: this.setTenantIdHeader(new HttpHeaders(), tenantId) })
      .pipe(
        map((utilityToken) => {
          const tokenEnts = utilityToken.tokenEntitlements.map((ent) => {
            return { ...ent, id: ent.entitlementId };
          });
          const disabledTokenEnts = utilityToken.disabledTokenEntitlements.map((ent) => {
            return { ...ent, id: ent.entitlementId };
          });
          return {
            id: utilityToken.id,
            tokenBalance: utilityToken.tokenBalance,
            tokenExpiration: utilityToken.tokenExpiration,
            tokenEntitlements: tokenEnts,
            disabledTokenEntitlements: disabledTokenEnts,
          };
        }),
        catchError((error: unknown) => this.handleError$(error)),
      );
  }

  getSubOrgList$(
    pageRequest: PaginatedRequest,
    sortFilterContext: SortFilterContext,
    all?: boolean,
    query?: string,
  ): Observable<TenantListPage<ITenantDetail>> {
    const params = new HttpParams()
      .set('perPage', pageRequest.perPage ?? 10)
      .set('pageNum', pageRequest.pageNum ?? 1)
      .set('sortFilterOpts', JSON.stringify(sortFilterContext ?? {}));

    const queryParams = all ? params.set('all', all.toString()) : params;
    const finalParams = query ? queryParams.set('query', query) : queryParams;
    const parentTenantId = pageRequest.tenantId;
    const url = `${this.baseUrl}${ApiEndPoints.Tenant}`;

    return this.http
      .get<TenantListPage<ITenantDetail>>(`${url}children`, {
        headers: this.setTenantIdHeader(new HttpHeaders(), parentTenantId),
        params: finalParams,
      })
      .pipe(catchError((error: unknown) => this.handleError$(error)));
  }

  getTenantEffectiveRoles$(tenantId: string, userId: string): Observable<any> {
    const url = `${this.baseUrl}${ApiEndPoints.Users}${userId}/tenanteffectiveroles`;
    return this.http
      .get<EffectiveRole[]>(url, {
        headers: this.setTenantIdHeader(new HttpHeaders(), String(tenantId)),
      })
      .pipe(catchError((error: unknown) => this.handleError$(error)));
  }

  private handleError$(error: unknown): Observable<any> {
    this.logger.error('error: ', error);
    const httpError = error as HttpErrorResponse;

    // client errors
    if (httpError.status === 400) {
      const message = 'Bad Request: The request was invalid';
      this.snackBarFacade.displayMessage({
        message: message,
        type: 'Error',
      });
      return throwError(() => error);
    }

    if (httpError.status === 403) {
      let message = 'Insufficient Permissions. Redirecting to dashboard';
      if (this.router.url === '/home') {
        message = 'Insufficient Permissions';
      }
      this.snackBarFacade.displayMessage({
        message: message,
        type: 'Error',
      });
      this.router.navigate(['/home']);
      return throwError(() => error);
    }

    this.controlPageFacade.navigateToError(new ErrorContextImpl(httpError));
    return throwError(() => error);
  }
}

export const ApiEndPoints = {
  Version: '/version',
  AccessRequest: '/accessrequests',
  Catalog: '/configuration/catalogs',
  Eula: '/eula/',
  Entitlement: '/entitlement/',
  Feedback: '/feedbacks',
  Invitations: '/invitations/',
  Notification: '/api/messages/',
  NotificationCounts: '/api/messages/count',
  SuggestedTenantByCode: '/tenants/invitecode/',
  SuggestedTenants: '/tenants/suggested/',
  Tenant: '/tenant/',
  Tenants: '/tenants/',
  Users: '/users/',
  CustomerSupport: '/customersupport/',
  UtilityTokens: '/tenant/utilitytokens',
  TrialBundles: '/configuration/trialbundles',
};

export const CustomerSupportEndpoints = {
  InviteAdmin: '/customersupport/inviteuser',
};
